import { useEffect, useRef, useState } from 'react'
import {
  useGetCustomAgentRunQuery,
  useListCustomAgentResultItemsQuery,
  useListCustomAgentResultsQuery,
  type CustomAgentResultItem
} from '@axilon-apps/domains-ontology-ui-state'

export function findResultItemAtRevision(
  current: CustomAgentResultItem,
  items: readonly CustomAgentResultItem[]
): CustomAgentResultItem | null {
  return (
    items.find(
      (row) =>
        row.run_id === current.run_id &&
        ((current.item_id && row.item_id === current.item_id) ||
          row.id === current.id ||
          (row.type === current.type && row.position === current.position))
    ) ?? null
  )
}

/** Omit `revision` when current is unknown so the proxy uses the run's live snapshot. */
export function revisionQueryArg(
  viewRevision: number | null,
  currentRevision: number | undefined
): number | undefined {
  if (viewRevision == null || viewRevision < 1) return undefined
  if (currentRevision != null && viewRevision === currentRevision) return undefined
  return viewRevision
}

/**
 * GET-run revision metas plus the selected snapshot for one parent run.
 * Always loads that run's document/items (current or historical) so Insights
 * does not keep showing a stale first emit after follow-up revises it.
 */
export function useInsightRunRevision(runId: string | null, configId: string | null) {
  const [viewRevision, setViewRevision] = useState<number | null>(null)
  const seenCurrentSnapshotRef = useRef<{
    runId: string
    revision: number
    activeFollowUpRunId: string | null
  } | null>(null)

  useEffect(() => {
    setViewRevision(null)
  }, [runId])

  const { data: run } = useGetCustomAgentRunQuery(runId ?? '', { skip: !runId })
  const knownCurrent =
    run?.current_revision && run.current_revision > 0 ? run.current_revision : undefined
  const activeFollowUpRunId = run?.active_follow_up_run_id ?? null
  const currentRevision = knownCurrent ?? 1
  const selectedRevision = viewRevision ?? currentRevision
  const viewingHistory = Boolean(runId && knownCurrent && selectedRevision < knownCurrent)
  const revision = revisionQueryArg(viewRevision, knownCurrent)

  const docsQuery = useListCustomAgentResultsQuery(
    {
      configId: configId ?? '',
      runId: runId ?? '',
      pageSize: 1,
      revision
    },
    { skip: !configId || !runId }
  )
  const itemsQuery = useListCustomAgentResultItemsQuery(
    {
      configId: configId ?? '',
      runId: runId ?? '',
      pageSize: 200,
      revision
    },
    { skip: !configId || !runId }
  )
  const refetchDocs = docsQuery.refetch
  const refetchItems = itemsQuery.refetch

  useEffect(() => {
    if (!runId || knownCurrent == null) return
    const previous = seenCurrentSnapshotRef.current
    seenCurrentSnapshotRef.current = {
      runId,
      revision: knownCurrent,
      activeFollowUpRunId
    }
    const revisionChanged = previous?.runId === runId && previous.revision !== knownCurrent
    const followUpFinished =
      previous?.runId === runId &&
      previous.activeFollowUpRunId !== null &&
      activeFollowUpRunId === null
    if (viewRevision == null && (revisionChanged || followUpFinished)) {
      void refetchDocs()
      void refetchItems()
    }
  }, [runId, knownCurrent, activeFollowUpRunId, viewRevision, refetchDocs, refetchItems])

  return {
    currentRevision,
    selectedRevision,
    viewingHistory,
    revisions: run?.result_revisions,
    setViewRevision,
    revisionDocument: docsQuery.data?.results?.[0],
    revisionItems: itemsQuery.data?.results ?? [],
    isLoadingRevision: viewingHistory && (docsQuery.isFetching || itemsQuery.isFetching),
    isRevisionError: viewingHistory && (docsQuery.isError || itemsQuery.isError),
    showPicker: currentRevision > 1
  }
}
